/**
 * AddSiteDialog — extracted from Clients.jsx in iter170 component
 * split step #2. The "Add Site" form (~187 LOC) opens from the
 * Customer detail Sheet and creates a new site for the active
 * customer. The dialog owns NO state of its own — siteFormData,
 * siteContactMode, and allCustomerContacts are owned by Clients.jsx
 * so they stay in sync with the contact roster and tax-rate lookups.
 *
 * Why props instead of a context bag: the dialog is opened in
 * exactly one place and the prop list is mechanical. Adding a
 * context provider would obscure what data this dialog actually
 * touches.
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { lookupZip, lookupTaxRate } from "@/lib/api";
import { formatPhoneNumber } from "@/lib/phoneFormat";

export function AddSiteDialog({
  open,
  onOpenChange,
  siteFormData,
  setSiteFormData,
  siteContactMode,
  setSiteContactMode,
  allCustomerContacts,
  facilityTypes,
  handleAddSite,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Site</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleAddSite} className="space-y-4">
          <div>
            <Label>Site Name *</Label>
            <Input
              value={siteFormData.name}
              onChange={(e) => setSiteFormData({ ...siteFormData, name: e.target.value })}
              required
              data-testid="input-site-name"
            />
          </div>
          <div>
            <Label>Address *</Label>
            <Input
              value={siteFormData.address}
              onChange={(e) => setSiteFormData({ ...siteFormData, address: e.target.value })}
              required
              autoComplete="off"
              data-testid="input-site-address"
            />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <Label>City</Label>
              <Input
                value={siteFormData.city}
                autoComplete="off"
                onChange={(e) => setSiteFormData((d) => ({ ...d, city: e.target.value }))}
                onBlur={() => {
                  if (siteFormData.city.length > 2 && siteFormData.state.length >= 2) {
                    if (!siteFormData.zip_code) {
                      lookupZip(siteFormData.city, siteFormData.state)
                        .then((r) => { if (r.data.zip_code) setSiteFormData((d) => d.zip_code ? d : ({ ...d, zip_code: r.data.zip_code })); })
                        .catch(() => {});
                    }
                    lookupTaxRate({ city: siteFormData.city, state: siteFormData.state })
                      .then((r) => { if (r.data.tax_no) setSiteFormData((d) => ({ ...d, tax_authority: r.data.tax_no })); })
                      .catch(() => {});
                  }
                }}
              />
            </div>
            <div>
              <Label>State</Label>
              <Input
                value={siteFormData.state}
                autoComplete="off"
                onChange={(e) => setSiteFormData((d) => ({ ...d, state: e.target.value.toUpperCase() }))}
                onBlur={() => {
                  if (siteFormData.state.length >= 2 && siteFormData.city.length > 2) {
                    if (!siteFormData.zip_code) {
                      lookupZip(siteFormData.city, siteFormData.state)
                        .then((r) => { if (r.data.zip_code) setSiteFormData((d) => d.zip_code ? d : ({ ...d, zip_code: r.data.zip_code })); })
                        .catch(() => {});
                    }
                    lookupTaxRate({ city: siteFormData.city, state: siteFormData.state })
                      .then((r) => { if (r.data.tax_no) setSiteFormData((d) => ({ ...d, tax_authority: r.data.tax_no })); })
                      .catch(() => {});
                  }
                }}
              />
            </div>
            <div>
              <Label>ZIP</Label>
              <Input
                value={siteFormData.zip_code}
                onChange={(e) => setSiteFormData({ ...siteFormData, zip_code: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div>
              <Label>Tax Authority</Label>
              <Input
                value={siteFormData.tax_authority || ""}
                onChange={(e) => setSiteFormData({ ...siteFormData, tax_authority: e.target.value })}
                placeholder="Auto"
                className="font-mono text-xs"
                autoComplete="off"
              />
            </div>
          </div>
          <div>
            <Label>Facility Type</Label>
            <Select
              value={siteFormData.facility_type || "none"}
              onValueChange={(value) => setSiteFormData({ ...siteFormData, facility_type: value === "none" ? "" : value })}
            >
              <SelectTrigger data-testid="select-site-facility-type">
                <SelectValue placeholder="Select facility type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {/* #142 (Group G): skip blank-name rows so Radix never
                    receives an empty-string value. */}
                {facilityTypes.filter((t) => (t.name || "").trim()).map((type) => (
                  <SelectItem key={type.id} value={type.name}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="site_is_primary"
              checked={siteFormData.is_primary}
              onCheckedChange={(checked) => setSiteFormData({ ...siteFormData, is_primary: checked })}
              data-testid="checkbox-site-primary"
            />
            <Label htmlFor="site_is_primary" className="cursor-pointer text-sm">Set as Primary Site</Label>
          </div>
          <div>
            <Label>Site Contact</Label>
            <Select
              value={siteContactMode === "new" ? "__new__" : (siteFormData.contact_name || "__none__")}
              onValueChange={(val) => {
                if (val === "__new__") {
                  setSiteContactMode("new");
                  setSiteFormData((d) => ({ ...d, contact_name: "", contact_phone: "", contact_email: "" }));
                } else if (val === "__none__") {
                  setSiteContactMode("existing");
                  setSiteFormData((d) => ({ ...d, contact_name: "", contact_phone: "", contact_email: "" }));
                } else {
                  setSiteContactMode("existing");
                  const c = allCustomerContacts.find((c) => c.id === val);
                  if (c) setSiteFormData((d) => ({ ...d, contact_name: c.name, contact_phone: c.phone || "", contact_email: c.email || "" }));
                }
              }}
            >
              <SelectTrigger className="h-9" data-testid="site-contact-select">
                <SelectValue placeholder="Select contact..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {allCustomerContacts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}{c.phone ? ` — ${c.phone}` : ""}{c._siteName ? ` (${c._siteName})` : ""}
                  </SelectItem>
                ))}
                <SelectItem value="__new__">+ Add New Contact</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {siteContactMode === "new" && (
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Name</Label>
                <Input
                  value={siteFormData.contact_name}
                  onChange={(e) => setSiteFormData({ ...siteFormData, contact_name: e.target.value })}
                  placeholder="Contact name"
                  autoComplete="off"
                  data-testid="site-new-contact-name"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={siteFormData.contact_phone}
                  onChange={(e) => setSiteFormData({ ...siteFormData, contact_phone: formatPhoneNumber(e.target.value) })}
                  placeholder="(303) 555-1234"
                  autoComplete="off"
                  data-testid="site-new-contact-phone"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={siteFormData.contact_email}
                  onChange={(e) => setSiteFormData({ ...siteFormData, contact_email: e.target.value })}
                  placeholder="email@example.com"
                  autoComplete="off"
                  data-testid="site-new-contact-email"
                />
              </div>
            </div>
          )}
          {siteContactMode === "existing" && siteFormData.contact_name && (
            <div className="text-xs text-slate-500">
              {siteFormData.contact_name}{siteFormData.contact_phone ? ` — ${siteFormData.contact_phone}` : ""}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" data-testid="btn-save-site">
              Add Site
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
